import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Public invite acceptance page — /invite/{token}.
 * Shows invitation details + account creation form.
 */
export default function InviteAcceptPage({ inviteToken }) {
  const { t } = useTranslation();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/aif/public/invite/${inviteToken}`);
        if (res.status === 410) { setError('expired'); return; }
        if (!res.ok) { setError('notfound'); return; }
        setInvite(await res.json());
      } catch { setError('network'); }
      finally { setLoading(false); }
    })();
  }, [inviteToken]);

  const handleAccept = async () => {
    if (!name || !password || password !== confirm) return;
    setAccepting(true);
    try {
      await apiClient.post(`/api/v1/aif/public/invite/${inviteToken}/accept`, { name, password }, { queueOnOffline: false });
      setDone(true);
    } catch (e) { setError(e.isNetworkError ? 'network' : 'failed'); }
    setAccepting(false);
  };

  if (loading) return <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)]"><div className="w-8 h-8 border-3 border-gray-200 border-t-green-600 rounded-full animate-spin" /></div>;

  if (error) {
    const msgs = { expired: t('members.inviteExpired'), notfound: t('members.inviteNotFound'), network: t('app.errorNetwork'), failed: t('members.acceptFailed') };
    return <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)] px-6"><div className="text-center space-y-3"><div className="text-4xl">{'\u26D4'}</div><p className="text-sm text-[var(--aif-text-secondary)]">{msgs[error]}</p></div></div>;
  }

  if (done) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)] px-6">
        <div className="text-center space-y-4">
          <div className="text-5xl">{'\u2705'}</div>
          <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('members.welcomeTitle')}</h2>
          <p className="text-sm text-[var(--aif-text-secondary)]">{t('members.welcomeMessage')}</p>
          <a href="/" className="inline-block px-6 py-3 rounded-xl bg-green-600 text-white font-medium text-sm">{t('auth.login')}</a>
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--aif-color-primary)]/30";

  return (
    <div className="min-h-[100dvh] bg-[var(--aif-bg-tertiary)] flex flex-col">
      <div className="bg-green-700 text-white px-6 py-5">
        <div className="text-lg font-bold flex items-center gap-2"><span>{'\uD83C\uDF3E'}</span> AI Farm</div>
      </div>
      <div className="flex-1 px-6 py-6 space-y-5 max-w-md mx-auto w-full">
        <div className="text-center space-y-2">
          <h2 className="text-base font-semibold text-[var(--aif-text-primary)]">
            {invite?.inviter_name} {t('members.invitedYou')}
          </h2>
          <p className="text-sm text-[var(--aif-text-secondary)]">
            {t('members.asRole')} <strong>{invite?.role}</strong> {t('members.at')} <strong>{invite?.enterprise_name}</strong>
            {invite?.betrieb_name && <span> ({invite.betrieb_name})</span>}
          </p>
        </div>

        <div className="space-y-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder={t('auth.name')} className={inputClass} />
          <input value={invite?.email || ''} readOnly className={`${inputClass} bg-[var(--aif-bg-tertiary)]`} />
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('auth.password')} className={inputClass} />
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('members.confirmPassword')} className={inputClass} />
          {password && confirm && password !== confirm && (
            <p className="text-xs text-red-500">{t('members.passwordMismatch')}</p>
          )}
        </div>

        <button
          onClick={handleAccept}
          disabled={accepting || !name || !password || password !== confirm}
          className="w-full py-3 rounded-xl bg-green-600 text-white font-medium text-sm disabled:opacity-50"
        >
          {accepting ? '...' : t('members.acceptInvite')}
        </button>
      </div>
    </div>
  );
}
